import { Modal } from '@components/base';
import Images from '@assets/images';
import css from './index.module.scss';

const ELIXIR_BASE = process.env.REACT_APP_ELIXIR_BASE_URL ?? 'https://elixir.elchemy.com';
const ELIXIR_FALLBACK = `${ELIXIR_BASE}/login/otp`;
const ELIXIR_ORDERS = `${ELIXIR_BASE}/tenant/external/orders`;

const DOMS_TO_ELIXIR_TAB: Record<string, string> = {
  order_overview: 'overview',
  documents: 'documents',
  tracking: 'tracking',
  stuffing_photos: 'stuffing-photos',
  people: 'people'
};

export function getElixirUrl(pathname: string, searchParams: URLSearchParams): string {
  if (pathname === '/order' || pathname.startsWith('/enquiry')) return ELIXIR_ORDERS;
  const match = pathname.match(/^\/order\/([^/]+)/);
  if (match) {
    const tab = DOMS_TO_ELIXIR_TAB[searchParams.get('activeTab') ?? ''] ?? 'overview';
    return `${ELIXIR_ORDERS}/${match[1]}?tab=${tab}`;
  }
  return ELIXIR_FALLBACK;
}

interface CustomerWelcomeModalProps {
  open: boolean;
  onClose: () => void;
  ctaUrl: string;
}

const StarIcon = () => (
  <svg
    width="12"
    height="12"
    viewBox="0 0 12 12"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    className={css.starIcon}
    aria-hidden="true"
  >
    <path
      d="M6 0.5L7.03 4.47L11 5.5L7.03 6.53L6 10.5L4.97 6.53L1 5.5L4.97 4.47L6 0.5Z"
      fill="#0f1013"
    />
  </svg>
);

const CustomerWelcomeModal = ({ open, onClose, ctaUrl }: CustomerWelcomeModalProps) => {
  const handleCta = () => {
    window.open(ctaUrl, '_blank', 'noopener,noreferrer');
    onClose();
  };

  return (
    <Modal open={open} disableEscapeKeyDown>
      <div className={css.modalWrapper}>
        <div className={css.header}>
          <p className={css.title}>Introducing Elixir 2.0</p>
          <button className={css.closeButton} onClick={onClose} aria-label="Close">
            <svg
              width="24"
              height="24"
              viewBox="0 0 24 24"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M18 6L6 18M6 6l12 12"
                stroke="#0f1013"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </button>
        </div>

        <div className={css.banner}>
          <img src={Images.elixirIcon} alt="Elixir" className={css.elixirLogo} />
          <div className={css.illustrationWrapper}>
            <img
              src={Images.v2popup}
              alt="Elixir 2.0 platform"
              className={`${css.illustration} ${css.illustrationDesktop}`}
            />
            <img
              src={Images.v2popupmobile}
              alt="Elixir 2.0 platform"
              className={`${css.illustration} ${css.illustrationMobile}`}
            />
          </div>
        </div>

        <div className={css.body}>
          <p className={css.bodyHeading}>{'We have a new & updated platform waiting for you!'}</p>
          <div className={css.featureGrid}>
            <div className={css.featureCol}>
              <div className={css.featureItem}>
                <StarIcon />
                <span>Track shipment &amp; order status anytime</span>
              </div>
              <div className={css.featureItem}>
                <StarIcon />
                <span>View documents &amp; stuffing photos in one centralized place</span>
              </div>
            </div>
            <div className={css.featureCol}>
              <div className={css.featureItem}>
                <StarIcon />
                <span>Reduce dependency on email follow-ups</span>
              </div>
              <div className={css.featureItem}>
                <StarIcon />
                <span>View our product catalogue along with specifications &amp; more</span>
              </div>
            </div>
          </div>
        </div>

        <div className={css.footer}>
          <button className={css.ctaButton} onClick={handleCta}>
            Explore Elixir
          </button>
        </div>
      </div>
    </Modal>
  );
};

export default CustomerWelcomeModal;
